import { Component, Ref } from 'vue-property-decorator'
import ConfigurationSectionHeader from '@/modules/configurationModule/ConfigurationSectionHeader.vue'
import VtGrid from '@/components/VtGrid/VtGrid.vue'
import { GetContextMenuItemsParams, MenuItemDef, RowClickedEvent, RowNode } from '@ag-grid-enterprise/all-modules'
import { BaseCrud } from '@/shared/models/cruds.models'
import BaseCrudServerSideComponent from '@/shared/resources/base-crud-server-side.component'
import { StrictNullable } from '@/shared/types/app.types'
import { ConfigurationBillableConceptGroupCreateRequest, ConfigurationBillableConceptGroupPatchInBatchRequest, ConfigurationBillableConceptGroupResponse } from '@/modules/configurationModule/detail/accounting/billable/groups-of-concepts/services/configuration-billable-concept-group.dto'
import {
    BillableConceptGroupGridState,
    ConceptGroupsGridSidepanelComboElements,
    initialBillableConceptGroupGridState
} from '@/modules/configurationModule/detail/accounting/billable/groups-of-concepts/components/grid/conceptgroups-grid.model'
import { configurationBillableConceptGroupService } from '@/modules/configurationModule/detail/accounting/billable/groups-of-concepts/services/configuration-billable-concept-group.service'
import ConceptGroupsModalBasicForm from '@/modules/configurationModule/detail/accounting/billable/groups-of-concepts/components/modal-basic-form/conceptgroups-modal-basic-form.vue'
import ConfigurationListSidepanelComp
    from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel'
import ConfigurationListSidepanel
    from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel.vue'
import {
    ConfigurationListSidePanelEntity
} from '@/modules/configurationModule/shared/components/configuration-list-sidepanel/configuration-list-sidepanel.model'
import { EntityIdentifierWithErrorResponse } from '@/shared/models/http/http.model'

@Component({
    components: {
        ConfigurationSectionHeader,
        ConceptGroupsModalBasicForm,
        VtGrid,
        ConfigurationListSidepanel
    }
})
export default class BillableConceptGroupGrid extends BaseCrudServerSideComponent<ConfigurationBillableConceptGroupResponse> implements BaseCrud {
    @Ref() readonly billableConceptGroupModalBasicFormWrapper!: any
    @Ref() readonly configurationListSidepanel!: ConfigurationListSidepanelComp

    state: BillableConceptGroupGridState
    constructor() {
        super()
        this.state = initialBillableConceptGroupGridState()
    }

    get cantUpdate(): boolean {
        return !this.canUpdate
    }

    get canUpdate(): boolean {
        return this.state.gridApi?.getSelectedRows().length === 1
    }

    get cantDelete(): boolean {
        return !this.canDelete
    }

    get canDelete(): boolean {
        return !!this.state.gridApi?.getSelectedRows().length
    }

    get canActivateBatch():boolean {
        return this.hasDeactiveEntity && !this.hasAllActivated
    }

    get hasDeactiveEntity():boolean {
        return !!this.state.selectedRows.find(entity => !entity.active)
    }

    get canDeactivateBatch():boolean {
        return this.state.selectedRows.length > 0 && this.hasActivePriority && !this.hasAllDeactivated
    }

    get hasActivePriority():boolean {
        return !!this.state.selectedRows.find(entity => entity.active)
    }

    getContextMenuItems(params: GetContextMenuItemsParams): MenuItemDef[] {
        if (!params?.node) {
            return []
        } else {
            return [
                {
                    name: this.$tc('configuration.actions.update'),
                    cssClasses: this.state.selectedRows.length > 1 ? ['not-show'] : [''],
                    action: () => this.onUpdate()
                },
                {
                    name: this.$tc('configuration.actions.delete'),
                    cssClasses: this.state.selectedRows.length < 1 ? ['not-show'] : [''],
                    action: () => this.deleteOperation(this.state.selectedRows)
                },
                {
                    name: this.$tc('configuration.actions.activate'),
                    cssClasses: this.canActivateBatch ? [''] : ['not-show'],
                    action: () => {
                        this.activateDeactivateSelection(true, this.state.selectedRows)
                    }
                },
                {
                    name: this.$tc('configuration.actions.deactivate'),
                    cssClasses: this.canDeactivateBatch ? [''] : ['not-show'],
                    action: () => {
                        this.activateDeactivateSelection(false, this.state.selectedRows)
                    }
                }
            ]
        }
    }

    onRowClicked(event: RowClickedEvent): void {
        const conceptGroup: ConfigurationBillableConceptGroupResponse = event.data
        this.configurationListSidepanel.show({
            title: conceptGroup.name,
            entities: [
                {
                    id: ConceptGroupsGridSidepanelComboElements.CONCEPTS,
                    entityKey: 'configuration.sections.concept'
                }
            ],
            obtainEntityList: async (entity: ConfigurationListSidePanelEntity): Promise<string[]> => {
                if (entity.id as ConceptGroupsGridSidepanelComboElements === ConceptGroupsGridSidepanelComboElements.CONCEPTS) {
                    return this._findConceptsOfGroup(conceptGroup.id)
                }
                return []
            }
        })
    }

    private async _findConceptsOfGroup(conceptGroupId: number): Promise<string[]> {
        const response = await this.$_toCallUnique(configurationBillableConceptGroupService.findConceptsOfGroup(conceptGroupId))
        return response?.content.map((item: ConfigurationBillableConceptGroupResponse) => item.name) ?? []
    }

    private async _patchConceptGroups(batchRequest: ConfigurationBillableConceptGroupPatchInBatchRequest[]): Promise<StrictNullable<EntityIdentifierWithErrorResponse[]>> {
        const response = await this.$_toCallUnique(this.state.service.patch(batchRequest))
        if (response && response.length !== batchRequest.length) {
            const elements = batchRequest.length - response.length
            if (elements > 0) {
                const choice = elements > 1 ? 2 : 1
                this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated', choice, { elements }))
            }
            return response
        }
        return null
    }

    async activateDeactivateSelection(option: boolean, selection: ConfigurationBillableConceptGroupResponse[]): Promise<void> {
        const selectionToModify = selection.filter(selected => selected.active !== option).map(selected => {
            selected.active = option
            return selected
        })
        const result = await this.$_toCallSecureLoading(configurationBillableConceptGroupService.patch(selectionToModify))
        if (result) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.updated'))
            this.reloadData()
        }
    }

    onAdd(): void {
        this.showModal(this.$tc('configuration.actions.add'), null, this.billableConceptGroupModalBasicFormWrapper)
    }

    onUpdate(): void {
        this.showModal(this.$tc('configuration.actions.update'), this.state.selectedRows[0], this.billableConceptGroupModalBasicFormWrapper)
    }

    async createOperation(row: ConfigurationBillableConceptGroupCreateRequest): Promise<void> {
        const result = await this.$_toCallSecureLoading(configurationBillableConceptGroupService.create(row))
        if (result) {
            this.$_showToastSuccess(this.$tc('configuration.toastMessages.created'))
            this.billableConceptGroupModalBasicFormWrapper.hide()
            this.reloadData()
        }
    }

    async patchOperation(batchRequest: ConfigurationBillableConceptGroupPatchInBatchRequest): Promise<void> {
        if (await this._patchConceptGroups([batchRequest])) {
            this.billableConceptGroupModalBasicFormWrapper?.hide()
            this.reloadData()
        }
    }

    async deleteSelectedRows(): Promise<void> {
        this.deleteOperation(this.state.selectedRows)
    }

    async deleteOperation(concepts: ConfigurationBillableConceptGroupResponse[]): Promise<void> {
        if (concepts.length) {
            const selectedRowsSize: number = this.state?.selectedRows?.length
            const labelConfirmation: string = this.$tc('configuration.grid.deleteConfirmation')
            const labelGrid: string = this.$tc('configuration.grid.element', selectedRowsSize)
            const labelDeleteQuestion: string = this.$tc('configuration.grid.deleteQuestion')
            const labelDeleteAction: string = this.$tc('configuration.actions.delete')
            const deleteConfirmation = await this.$_showConfirmationModal(
                `${labelConfirmation} ${selectedRowsSize} ${labelGrid}, <br/> ${labelDeleteQuestion}`,
                labelDeleteAction
            )

            if (deleteConfirmation) {
                const toRemove = {
                    entityIdentifiers: concepts.map(concept => concept.id)
                }
                const result = await this.$_toCallSecureLoading(configurationBillableConceptGroupService.delete(toRemove))
                if (result) {
                    this.$_showToastSuccess(this.$tc('configuration.toastMessages.deleted'))
                    this.reloadData()
                }
            }
        }
    }

    closeSidePanel():void {
        this.state.showSidepanel = false
    }
}
